import {
  useCallback,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { PaperclipIcon } from 'lucide-react'

import {
  PromptInput,
  PromptInputButton,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
} from '@/components/ui/shadcn-io/ai/prompt-input'
import { MessageAttachment, MessageAttachments } from '@/components/ai/message'
import { useModifierHeld } from '@/hooks/useModifierHeld'
import { useMountEffect } from '@/hooks/useMountEffect'
import { getModifierKey } from '@/lib/platform'
import {
  ACCEPT_ATTR,
  MAX_ATTACHMENTS,
  toFileUIPart,
  type PendingAttachment,
} from '@/lib/attachments'
import {
  EMPTY_QUEUE,
  useMessageQueue,
  type QueuedMessage,
} from '@/stores/messageQueue'

import { AgentSwitcherChips } from './AgentSwitcherChips'
import { ComposerSubmitButton } from './ComposerSubmitButton'
import { QueuedMessagesControl } from './QueuedMessagesControl'
import { useQueuedMessageEdit } from './useQueuedMessageEdit'
import {
  DEFAULT_FOLLOW_UP,
  resolveComposerAction,
  type ComposerAction,
} from './composerDispatch'

/** David only: what the composer does with text typed while a run is active. */
export interface FollowUpHandlers {
  conversationId: string
  onQueue: (text: string) => void
  /** Returns false when the steer was refused and the text should stay. */
  onSteer: (text: string) => boolean
  onPromote: (item: QueuedMessage) => void
}

interface AgentChatInputProps {
  handleSendMessage: (input: string) => void
  isStreaming: boolean
  onStop: () => void
  placeholder?: string
  /** Files picked for the next message. Owned by AgentChat: this component is
   *  remounted after every send, which would wipe state held here. */
  attachments?: PendingAttachment[]
  onAddFiles?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  /** True while a clarifying card is open — that path can't carry files. */
  attachmentsDisabled?: boolean
  /** Take back the focus a send's remount dropped. Never takes it from an
   *  element the user moved to, such as a thread picked in the sidebar. */
  autoFocus?: boolean
  /** Absent: the composer disables while a run is active (Hanna). */
  followUp?: FollowUpHandlers
}

// Shown only while David works: the state the hint is about. The chord sits
// here and in the button tooltip; the queue banner covers the rest.
const FOLLOW_UP_PLACEHOLDER = `Queue a follow-up, ${getModifierKey()}+Enter to send now`

export function AgentChatInput({
  handleSendMessage,
  isStreaming,
  onStop,
  placeholder,
  attachments = [],
  onAddFiles,
  onRemoveAttachment,
  attachmentsDisabled = false,
  followUp,
  autoFocus,
}: AgentChatInputProps) {
  const [input, setInput] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const modifierHeld = useModifierHeld()
  useMountEffect(() => {
    if (autoFocus && document.activeElement === document.body) {
      textareaRef.current?.focus()
    }
  })
  const conversationId = followUp?.conversationId
  const queue =
    useMessageQueue((s) =>
      conversationId ? s.queues[conversationId] : undefined
    ) ?? EMPTY_QUEUE
  const { resume } = useMessageQueue.getState()

  const { beginEdit, cancelEdit, commitEdit } = useQueuedMessageEdit({
    conversationId,
    input,
    setInput,
    textareaRef,
  })

  const canSend =
    Boolean(input.trim()) || (attachments.length > 0 && !attachmentsDisabled)

  const resolve = (alternate: boolean): ComposerAction => {
    if (!followUp) return isStreaming ? 'stop' : 'send'
    return resolveComposerAction({
      isRunActive: isStreaming,
      hasText: canSend,
      alternate,
      editingId: queue.editingId,
      held: queue.held,
      defaultFollowUp: DEFAULT_FOLLOW_UP,
    })
  }
  const action = resolve(modifierHeld)

  const run = (alternate: boolean) => {
    const resolved = resolve(alternate)
    if (resolved === 'send') handleSendMessage(input)
    else if (resolved === 'stop') onStop()
    else if (followUp && conversationId) {
      if (resolved === 'queue') {
        followUp.onQueue(input)
        setInput('')
      } else if (resolved === 'steer') {
        if (followUp.onSteer(input)) setInput('')
      } else if (resolved === 'update' && queue.editingId && input.trim()) {
        commitEdit(queue.editingId, input.trim())
      } else if (resolved === 'resume') {
        resume(conversationId)
      }
    }
    // A click on the round button takes focus; the caret belongs in the composer.
    textareaRef.current?.focus()
  }

  // Stop and Resume are click-only: an empty Enter must not kill the run.
  const runFromKeyboard = (alternate: boolean) => {
    if (canSend) run(alternate)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    runFromKeyboard(false)
  }

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    run(e.metaKey || e.ctrlKey)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!followUp || !conversationId) return
    const mod = e.metaKey || e.ctrlKey
    const { items, editingId } = queue
    const head = items[0]
    const last = items.at(-1)
    const caretAtStart =
      e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0
    if (e.key === 'Enter' && mod && e.shiftKey) {
      e.preventDefault()
      if (isStreaming && head) followUp.onPromote(head)
    } else if (e.key === 'Enter' && mod && isStreaming) {
      e.preventDefault()
      runFromKeyboard(true)
    } else if (e.key === 'Escape' && editingId) {
      e.preventDefault()
      cancelEdit()
    } else if (
      e.key === 'ArrowUp' &&
      e.altKey &&
      !editingId &&
      last &&
      caretAtStart
    ) {
      e.preventDefault()
      beginEdit(last)
    }
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setInput(e.currentTarget.value),
    []
  )

  const handleFilesPicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onAddFiles?.(Array.from(e.currentTarget.files ?? []))
      // Reset so picking the same file twice in a row still fires onChange.
      e.currentTarget.value = ''
    },
    [onAddFiles]
  )

  // Pasting a screenshot is the highest-value entry point in practice.
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData.files)
      if (files.length === 0 || attachmentsDisabled) return
      if (!e.clipboardData.getData('text/plain')) e.preventDefault()
      onAddFiles?.(files)
    },
    [onAddFiles, attachmentsDisabled]
  )

  return (
    <div className="flex flex-col p-4">
      {followUp && (
        <QueuedMessagesControl
          conversationId={followUp.conversationId}
          isRunActive={isStreaming}
          onPromote={followUp.onPromote}
          onEdit={beginEdit}
          onCancelEdit={cancelEdit}
          composerRef={textareaRef}
        />
      )}
      <PromptInput
        onSubmit={handleSubmit}
        className="rounded-2xl shadow-card border-0 focus-within:ring-2 focus-within:ring-david-solid/30">
        {attachments.length > 0 && (
          <MessageAttachments
            aria-label="Attached files"
            className="ml-0 px-3 pt-3"
            role="list">
            {attachments.map((attachment) => (
              <MessageAttachment
                key={attachment.id}
                data={toFileUIPart(attachment)}
                onRemove={() => onRemoveAttachment?.(attachment.id)}
              />
            ))}
          </MessageAttachments>
        )}
        <PromptInputTextarea
          ref={textareaRef}
          className="min-h-0 text-base"
          disabled={isStreaming && !followUp}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={
            placeholder ??
            (isStreaming && followUp ? FOLLOW_UP_PLACEHOLDER : 'Ask anything')
          }
          value={input}
        />
        <PromptInputToolbar className="px-2 pb-2">
          {/* Override the base rounded-bl-xl so every toolbar button keeps 8px. */}
          <PromptInputTools className="[&_button:first-child]:rounded-bl-lg">
            <PromptInputButton
              aria-label="Attach files"
              className="size-7"
              disabled={
                (isStreaming && !followUp) ||
                attachmentsDisabled ||
                attachments.length >= MAX_ATTACHMENTS
              }
              onClick={() => fileInputRef.current?.click()}
              type="button">
              <PaperclipIcon className="size-4" />
            </PromptInputButton>
            <input
              accept={ACCEPT_ATTR}
              className="hidden"
              multiple
              onChange={handleFilesPicked}
              ref={fileInputRef}
              type="file"
            />
            <AgentSwitcherChips />
          </PromptInputTools>
          <ComposerSubmitButton
            action={action}
            disabled={!canSend}
            onClick={handleButtonClick}
            withTooltip={Boolean(followUp)}
          />
        </PromptInputToolbar>
      </PromptInput>
    </div>
  )
}
